import React from "react";
import { Info, List, Plus, Loader2 } from "lucide-react";
import SearchableSelect from "../SearchableSelect";
import {
  ACTION_CARD,
  ACTION_CARD_FOOTER,
  ACTION_DROPDOWN_RESERVE,
  ACTION_FRAME,
  ACTION_RAIL,
  ACTION_RAIL_ICON_BOX,
  ACTION_RAIL_ICON_SIZE,
  ACTION_RAIL_ITEM,
  ACTION_RAIL_LABEL,
  ACTION_RAIL_TEXT,
  ACTION_SELECT_WELL,
} from "./actionUiChrome";

/** Single guidance tip — use once per form, never on page headers that already explain the action. */
export function ActionInfoTip({ text }) {
  if (!text) return null;
  return (
    <span
      title={text}
      className="inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full border border-[hsl(var(--ds-primary)/0.35)] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
      aria-label={text}
    >
      <Info size={12} strokeWidth={2} className="h-3 w-3" />
    </span>
  );
}

/**
 * Process card: taller body, dropdown opens downward into reserved space; footer CTAs stay visible.
 */
export function ActionProcessCard({
  darkMode = false,
  title,
  infoText = "",
  selectId = "action-employee",
  employeeId,
  onEmployeeChange,
  employeeOptions = null,
  fetchOptions = null,
  loading = false,
  primaryLabel = "Continue",
  onPrimary,
  secondaryLabel = "View list",
  onSecondary = null,
  showSecondary = false,
  children = null,
  /** Optional slot under the employee field (e.g. recent chips). Defaults unused. */
  afterSelect = null,
  emptyMessage = null,
  /** "modern" softens radii for Attendance Actions UX; default keeps Payroll/Employee look. */
  tone = "default",
}) {
  const border =
    tone === "modern"
      ? "border-[hsl(var(--hrms-line))]"
      : "border-[hsl(var(--ds-primary)/0.35)]";
  const cardClass =
    tone === "modern"
      ? "flex min-h-[26rem] flex-col overflow-hidden rounded-[18px] border border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
      : ACTION_CARD;
  const footClass =
    tone === "modern"
      ? "relative z-[30] flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-b-[18px] border-t border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] px-5 py-3.5"
      : ACTION_CARD_FOOTER;
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-start pb-4 pt-1">
      <div className={cardClass}>
        <div className="relative z-0 flex flex-1 flex-col space-y-4 p-5 pb-3">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
              {title}
            </h3>
            <ActionInfoTip text={infoText} />
          </div>
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-xs text-[hsl(var(--ds-muted-fg))]">
              <Loader2 size={14} className="animate-spin" /> Loading employees…
            </div>
          ) : emptyMessage ? (
            <div className={`rounded-lg border border-dashed px-4 py-10 text-center text-sm ${border}`}>
              {emptyMessage}
            </div>
          ) : (
            <>
              <div className={ACTION_SELECT_WELL}>
                <SearchableSelect
                  id={selectId}
                  label="Employee"
                  value={employeeId}
                  onChange={onEmployeeChange}
                  staticOptions={employeeOptions || undefined}
                  fetchOptions={fetchOptions || undefined}
                  isEmployee={Boolean(fetchOptions) && !employeeOptions}
                  darkMode={darkMode}
                  compact
                  required
                  placeholder="Search code or name…"
                  menuPlacement="bottom"
                  menuPortalTarget={null}
                  inActionCard
                />
              </div>
              {afterSelect}
              <div className={ACTION_DROPDOWN_RESERVE} aria-hidden="true" />
            </>
          )}
          {children}
        </div>
        <div className={footClass}>
          {showSecondary && onSecondary ? (
            <button
              type="button"
              onClick={onSecondary}
              className={`inline-flex items-center gap-1.5 border px-3 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))] ${
                tone === "modern" ? "rounded-[11px]" : "rounded-lg"
              }`}
            >
              <List size={13} /> {secondaryLabel}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={!employeeId || loading || !!emptyMessage}
            onClick={onPrimary}
            className={`inline-flex items-center gap-1.5 bg-[hsl(var(--ds-primary))] px-4 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] disabled:opacity-45 ${
              tone === "modern" ? "h-10 rounded-[11px]" : "rounded-lg py-2"
            }`}
          >
            <Plus size={14} /> {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Left action rail + body — shared by Employee / Payroll / module Actions.
 * tone="modern" = redesign fixed-height rows (no theme tint on inactive).
 */
export function ModuleActionsFrame({
  actions = [],
  activeKey,
  onSelect,
  icons = {},
  children,
  tone = "default",
  title = "Actions",
}) {
  const modern = tone === "modern";
  const frameClass = modern
    ? "flex min-h-0 flex-1 overflow-hidden rounded-[18px] border border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)]"
    : ACTION_FRAME;
  const railClass = modern
    ? "flex w-[15.5rem] shrink-0 flex-col border-r border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))]"
    : ACTION_RAIL;

  return (
    <div className={frameClass}>
      <aside className={railClass}>
        <div className="px-2 py-2">
          <p
            className={
              modern
                ? "px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--hrms-mute))]"
                : ACTION_RAIL_LABEL
            }
          >
            {title}
          </p>
        </div>
        <nav className="admin-shell-scrollbar min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
          <ul className="space-y-0.5">
            {actions.map((a, idx) => {
              const Icon3d = icons[a.key];
              const selected = a.key === activeKey;
              const dotTone = ["present", "leave", "absent", "half", "wo"][idx % 5];
              return (
                <li key={a.key} className={modern ? "h-10" : undefined}>
                  <button
                    type="button"
                    onClick={() => onSelect(a.key)}
                    aria-current={selected ? "true" : undefined}
                    className={`${
                      modern
                        ? "flex h-10 w-full shrink-0 items-center gap-2.5 rounded-[11px] px-2.5 text-left transition-colors"
                        : ACTION_RAIL_ITEM
                    } ${
                      selected
                        ? modern
                          ? "bg-[hsl(var(--hrms-acc)/0.12)] shadow-[inset_3px_0_0_hsl(var(--hrms-acc))]"
                          : "bg-[hsl(var(--ds-primary)/0.1)] ring-1 ring-[hsl(var(--ds-primary)/0.4)]"
                        : modern
                          ? "hover:bg-[hsl(var(--hrms-ink)/0.04)]"
                          : "hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    {modern ? (
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                          dotTone === "present"
                            ? "bg-[hsl(var(--hrms-present))]"
                            : dotTone === "leave"
                              ? "bg-[hsl(var(--hrms-leave))]"
                              : dotTone === "absent"
                                ? "bg-[hsl(var(--hrms-absent))]"
                                : dotTone === "half"
                                  ? "bg-[hsl(var(--hrms-half))]"
                                  : "bg-[hsl(var(--hrms-wo))]"
                        }`}
                        aria-hidden
                      />
                    ) : (
                      <span className={ACTION_RAIL_ICON_BOX}>
                        {Icon3d ? <Icon3d size={ACTION_RAIL_ICON_SIZE} /> : null}
                      </span>
                    )}
                    <span
                      className={`${
                        modern
                          ? "min-w-0 flex-1 truncate text-[13px] font-semibold leading-none tracking-tight"
                          : ACTION_RAIL_TEXT
                      } ${
                        selected
                          ? modern
                            ? "text-[hsl(var(--hrms-acc))]"
                            : "text-[hsl(var(--ds-primary))]"
                          : modern
                            ? "text-[hsl(var(--hrms-ink))]"
                            : "text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {a.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-surface))] p-3">
        {children}
      </section>
    </div>
  );
}
